import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
export const audioSrc = (a) => `${api.defaults.baseURL}${a.url.replace(/^\/api/, "")}`;

export function AudioRecorder({ audio, onChange }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [uploading, setUploading] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("Audio recording is not supported in this browser");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const duration = secsRef.current;
        setRecording(false);
        if (!blob.size) return;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], `voice-note-${Date.now()}.${ext}`, { type: type.split(";")[0] }));
          const { data } = await api.post("/uploads/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
          onChange([...(audio || []), { id: data.id, url: data.url, filename: data.filename, duration }]);
          toast.success("Voice note attached");
        } catch (e) { toast.error(e?.response?.data?.detail || "Audio upload failed"); }
        finally { setUploading(false); }
      };
      recRef.current = rec;
      rec.start();
      setSecs(0); secsRef.current = 0;
      timerRef.current = setInterval(() => { secsRef.current += 1; setSecs(secsRef.current); }, 1000);
      setRecording(true);
    } catch { toast.error("Microphone access was denied"); }
  };
  const secsRef = useRef(0);
  const stop = () => { if (recRef.current?.state === "recording") recRef.current.stop(); };

  return (
    <div className="space-y-2" data-testid="audio-recorder">
      <div className="flex items-center gap-3">
        {!recording ? (
          <button type="button" onClick={start} disabled={uploading} data-testid="record-audio-button"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-rose-600 text-white text-sm font-medium disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}{uploading ? "Uploading…" : "Record Audio"}
          </button>
        ) : (
          <button type="button" onClick={stop} data-testid="stop-audio-button"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-slate-800 text-white text-sm font-medium">
            <Square className="h-4 w-4 fill-current" />Stop <span className="font-mono tabular-nums" data-testid="audio-timer">{fmt(secs)}</span>
            <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
          </button>
        )}
        <span className="text-xs text-slate-500">{recording ? "Recording… dictate your notes" : "Dictate notes from the field"}</span>
      </div>
      {(audio || []).map((a) => (
        <div key={a.id} className="flex items-center gap-2" data-testid={`audio-item-${a.id}`}>
          <audio controls preload="none" src={audioSrc(a)} className="h-9 flex-1 min-w-0" />
          {a.duration ? <span className="text-xs text-slate-500 font-mono">{fmt(a.duration)}</span> : null}
          <button type="button" onClick={() => onChange(audio.filter((x) => x.id !== a.id))} className="text-slate-400 hover:text-rose-600" data-testid={`remove-audio-${a.id}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}
